/**
 * RestoID — MFA authenticator-app (TOTP) binding.
 * ---------------------------------------------------------------------------
 * RestoID frame around the PRISTINE upstream TOTP binding data (`secretQrCode`,
 * `secret`) + `custom/components/TotpCodeEntry` (upstream's `useSendMfaPayload`
 * submit and `session.mfa.invalid_totp_code` branch, surfaced as a toast) + the
 * shared RestoID `MfaSwitchLink`. Replaces Logto's "Step 1 / Step 2"
 * default layout with the design's QR + key + code blocks.
 * Swapped in for `./pages/MfaBinding/TotpBinding` via `src/App.tsx`.
 */
import { VerificationType } from '@logto/schemas';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { validate } from 'superstruct';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import useTextHandler from '@/hooks/use-text-handler';
import { UserMfaFlow } from '@/types';
import { totpBindingStateGuard } from '@/types/guard';

import { MfaSwitchLink } from '../components/MfaFactorRows';
import TotpCodeEntry from '../components/TotpCodeEntry';
import { C, FONT, Icon, ScreenHead, Stack } from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

const label: React.CSSProperties = {
  display: 'block',
  color: C.sub,
  font: `500 13px/1 ${FONT}`,
  marginBottom: 10,
};

const MfaTotpBinding = () => {
  const { t } = useTranslation();
  const c = useCopy();
  const navigate = useNavigateWithPreservedSearchParams();
  const { state } = useLocation();
  const [, totpBindingState] = validate(state, totpBindingStateGuard);
  const { verificationIdsMap } = useContext(UserInteractionContext);
  const verificationId = verificationIdsMap[VerificationType.TOTP];

  const { copyText } = useTextHandler();
  const [copied, setCopied] = useState(false);

  if (!totpBindingState || !verificationId) {
    return <ErrorPage title="error.invalid_session" />;
  }

  const { secret, secretQrCode, availableFactors, skippable } = totpBindingState;

  const onCopy = () => {
    void copyText(secret, t('mfa.secret_key_copied'));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'mfa.add_authenticator_app' }}>
      <Stack gap={20}>
        <ScreenHead
          back={() => navigate(-1)}
          backLabel={c('backShort')}
          title={t('mfa.add_authenticator_app')}
        />

        {/* Scan the QR */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {secretQrCode ? (
            <span
              style={{
                flex: '0 0 auto',
                width: 116,
                height: 116,
                borderRadius: 14,
                // The QR needs a true white quiet zone to stay scannable in any theme.
                background: '#ffffff',
                padding: 9,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <img src={secretQrCode} alt="QR" style={{ width: '100%', height: '100%' }} />
            </span>
          ) : null}
          <p style={{ margin: 0, color: C.sub, font: `400 14px/1.5 ${FONT}` }}>{c('totpQrStep')}</p>
        </div>

        {/* Or paste the key */}
        <div>
          <span style={label}>{c('totpKeyStep')}</span>
          <button
            type="button"
            onClick={onCopy}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              width: '100%',
              padding: '13px 14px',
              borderRadius: 12,
              cursor: 'pointer',
              background: C.field,
              border: `1px solid ${C.line}`,
            }}
          >
            <span
              style={{
                flex: 1,
                minWidth: 0,
                textAlign: 'left',
                color: C.text,
                font: `500 14px/1.4 "SF Mono", ui-monospace, Menlo, monospace`,
                letterSpacing: '0.12em',
                wordBreak: 'break-all',
              }}
            >
              {secret}
            </span>
            <span
              style={{
                flex: '0 0 auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                color: copied ? C.brand : C.sub,
                font: `500 12.5px/1 ${FONT}`,
              }}
            >
              <Icon name={copied ? 'check' : 'copy'} size={15} />
              {copied ? <span>{c('totpCopied')}</span> : null}
            </span>
          </button>
        </div>

        {/* Enter the code */}
        <div>
          <span style={label}>{c('totpEnterCode')}</span>
          <TotpCodeEntry flow={UserMfaFlow.MfaBinding} verificationId={verificationId} />
        </div>

        <MfaSwitchLink flow={UserMfaFlow.MfaBinding} flowState={{ availableFactors, skippable }} />
      </Stack>
    </FirstScreenLayout>
  );
};

export default MfaTotpBinding;
